import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, ViewContainerRef } from '@angular/core';
import { catchError, Observable } from 'rxjs';
import { environment } from '../../../../../../environments/environment.development';
import { ApiResponse } from '../../../../../core/models/api-response.models';
import { ErrorHandlingService } from '../../../../../core/services/error-handling.service';
import { ReportRes } from './report-manager.interface';
import { FrequencyDatasheetGraphComponent } from '../../insights-data/data-graph/frequency-datasheet-graph/frequency-datasheet-graph.component';
import { LatencyGraphComponent } from '../../insights-data/data-graph/latency-graph/latency-graph.component';
import { PartialIntervalGraphComponent } from '../../insights-data/data-graph/partial-interval-graph/partial-interval-graph.component';
import { ReplacementBehaviorGraphComponent } from '../../insights-data/data-graph/replacement-behavior-graph/replacement-behavior-graph.component';
import {
  frequencyDatasheetGraph,
  GraphList,
  partialIntervalGraph,
  replacementDataType,
  frequencyIntervalGraph,
  BehaviorGraphDataResponse,
  latencyGraph,
  PrintCommentData,
} from '../../insights-data/insights-data.interface';
import { FrequencyIntervalGraphComponent } from '../../insights-data/data-graph/frequency-interval-graph/frequency-interval-graph.component';

@Injectable({
  providedIn: 'root',
})
export class ReportManagerService {
  private readonly API_URL = `${environment.apiUrl}/BehaviorReport`;
  constructor(
    private readonly errorHandler: ErrorHandlingService,
    private readonly http: HttpClient,
    private readonly injector: Injector
  ) {}

  getReports(caseId: number) {
    return this.http
      .get<
        ApiResponse<ReportRes[]>
      >(`${this.API_URL}/progress-report-manager?caseId=${caseId}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  deleteReport(id: number): Observable<ApiResponse<void>> {
    return this.http
      .delete<
        ApiResponse<void>
      >(`${this.API_URL}/progress-report-manager?progressReportId=${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  createReport(payload: ReportRes): Observable<ApiResponse<void>> {
    return this.http
      .put<
        ApiResponse<void>
      >(`${this.API_URL}/progress-report-manager`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  generateGraphImage(
    viewContainerRef: ViewContainerRef,
    selectedUnit: number,
    selectedGraph: number,
    checkedGraphList: GraphList[],
    graphData:
      | frequencyDatasheetGraph
      | latencyGraph
      | partialIntervalGraph
      | replacementDataType
      | frequencyIntervalGraph,
    showIntervalFrequency: boolean,
    unitName = '',
    caseBehaviorId = 0,
    behaviorData: BehaviorGraphDataResponse
  ): Promise<GraphList[]> {
    return new Promise(resolve => {
      let componentFactory: import('@angular/core').Type<
        | FrequencyDatasheetGraphComponent
        | FrequencyIntervalGraphComponent
        | LatencyGraphComponent
        | PartialIntervalGraphComponent
        | ReplacementBehaviorGraphComponent
      >;
      switch (selectedUnit) {
        case 1:
          if (showIntervalFrequency) {
            componentFactory = FrequencyIntervalGraphComponent;
          } else {
            componentFactory = FrequencyDatasheetGraphComponent;
          }
          break;
        case 2:
          componentFactory = LatencyGraphComponent;
          break;
        case 3:
          componentFactory = PartialIntervalGraphComponent;
          break;
        case 4:
          componentFactory = ReplacementBehaviorGraphComponent;
          break;
        default:
          resolve(checkedGraphList);
          return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const factory: any = viewContainerRef.createComponent(componentFactory, {
        injector: this.injector,
      });
      const componentElement = factory.location.nativeElement;
      componentElement.style.display = 'none';
      factory.instance.graphData = graphData;

      let imagesResolved = 0;
      const totalImages = selectedUnit === 3 ? 2 : 1;

      const checkResolve = () => {
        imagesResolved++;
        if (imagesResolved === totalImages) {
          resolve(checkedGraphList);
          factory.destroy();
        }
      };
      if (selectedUnit === 3) {
        factory.instance.allChartTotalImage.subscribe((images: string[]) => {
          const graph = checkedGraphList.find(
            g => g.unitName === unitName && g.caseBehaviorId === caseBehaviorId
          );
          if (graph) {
            graph.graphImageTotal = [
              ...(graph.graphImageTotal || []),
              ...images,
            ];
          }
          checkResolve();
        });
      }

      factory.instance.allChartImage.subscribe((images: string[]) => {
        const graph = checkedGraphList.find(
          g => g.unitName === unitName && g.caseBehaviorId === caseBehaviorId
        );
        if (graph) {
          graph.selectedGraph = selectedGraph;
          graph.selectedUnit = selectedUnit;
          graph.graphImage = [...(graph.graphImage || []), ...images];
          graph.graphData = graphData;
          // Map graph comments into grouped commentsData for printing/reporting
          const rawComments = (
            graphData as unknown as { comments?: PrintCommentData[] }
          )?.comments;
          graph.commentsData =
            !showIntervalFrequency &&
            Array.isArray(rawComments) &&
            rawComments.length > 0
              ? Object.entries(
                  rawComments.reduce(
                    (acc, comment) => {
                      // Use dataDate if present; fallback to createdOn (date portion)
                      const dateKey =
                        comment.dataDate ||
                        (comment.createdOn
                          ? String(comment.createdOn).split('T')[0]
                          : '');
                      if (!dateKey) {
                        return acc;
                      }
                      if (!acc[dateKey]) {
                        acc[dateKey] = [];
                      }
                      acc[dateKey].push({
                        id: (comment.commentId ??
                          comment.commentItemId ??
                          0) as number,
                        comment: comment.commentText,
                        createdBy: comment.createdBy,
                        createdOn: comment.createdOn,
                        createdByName: comment.createdByName,
                      });
                      return acc;
                    },
                    {} as Record<
                      string,
                      {
                        id: number;
                        comment: string;
                        createdBy: number;
                        createdOn: string;
                        createdByName: string;
                      }[]
                    >
                  )
                ).map(([dataDate, comments], index) => ({
                  activityDataUnitId: behaviorData.id || 0,
                  dataDate,
                  id: index,
                  comments,
                }))
              : graph.commentsData || [];
          if (showIntervalFrequency) {
            graph.intervalData = graphData;
          } else {
            graph.data = graphData;
          }
        }
        checkResolve();
      });
    });
  }
  getValidDateRange(
    minDate: string | number | Date,
    maxDate: string | number | Date,
    formStartDate: string | number | Date,
    formEndDate: string | number | Date
  ): { startDate: Date; endDate: Date } {
    // Convert inputs to Date objects
    const min = new Date(minDate);
    const max = new Date(maxDate);
    const start = new Date(formStartDate);
    const end = new Date(formEndDate);

    // Ensure startDate is within minDate and maxDate range
    const startDate = start < min ? min : start > max ? max : start;

    // Ensure endDate is within minDate and maxDate range
    const endDate = end > max ? max : end < min ? min : end;

    return { startDate, endDate };
  }
}
